import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Bell, LayoutDashboard, Search, ShoppingCart, User } from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useNotifications } from '../contexts/NotificationContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { categoriesAPI } from '../lib/api';
import { fetchWithCache } from '../lib/cache';

const RECENT_SEARCH_KEY = 'pwa_recent_searches';
const MAX_RECENT = 5;

export default function PwaCompactHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { getItemCount } = useCart();
  const { unreadCount } = useNotifications();
  const [searchQuery, setSearchQuery] = useState('');
  const [recent, setRecent] = useState([]);
  const [categoryMap, setCategoryMap] = useState({});
  const haptic = () => {
    if (window.navigator?.vibrate) {
      window.navigator.vibrate(6);
    }
  };

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(RECENT_SEARCH_KEY) || '[]');
      if (Array.isArray(stored)) {
        setRecent(stored.slice(0, MAX_RECENT));
      }
    } catch {
      setRecent([]);
    }
  }, []);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        await fetchWithCache(
          'pwa_header_categories',
          async () => {
            const response = await categoriesAPI.getTree();
            const data = Array.isArray(response.data)
              ? response.data
              : (Array.isArray(response.data?.categories) ? response.data.categories : []);
            return data;
          },
          (data) => {
            const map = {};
            const walk = (nodes = []) => {
              nodes.forEach((node) => {
                if (node?.id) {
                  map[String(node.id)] = node.name || `Category ${node.id}`;
                }
                if (Array.isArray(node.children)) {
                  walk(node.children);
                }
              });
            };
            walk(data);
            setCategoryMap(map);
          },
        );
      } catch {
        setCategoryMap({});
      }
    };

    loadCategories();
  }, []);

  const handleSearch = (event) => {
    event.preventDefault();
    const term = searchQuery.trim();
    if (term) {
      const next = [term, ...recent.filter((item) => item !== term)].slice(0, MAX_RECENT);
      setRecent(next);
      localStorage.setItem(RECENT_SEARCH_KEY, JSON.stringify(next));
      navigate(`/products?search=${encodeURIComponent(term)}`);
    }
  };

  const chips = useMemo(() => recent.filter(Boolean), [recent]);

  const crumbs = useMemo(() => {
    const path = location.pathname;
    if (path === '/' || path === '/home') return [];

    if (path.startsWith('/products')) {
      const term = searchParams.get('search');
      const category = searchParams.get('category');
      if (term) return [
        { label: 'Home', to: '/' },
        { label: 'Products', to: '/products' },
        { label: term, to: `/products?search=${encodeURIComponent(term)}` },
      ];
      if (category) {
        const name = categoryMap[String(category)] || `Category ${category}`;
        return [
          { label: 'Home', to: '/' },
          { label: 'Products', to: '/products' },
          { label: name, to: `/products?category=${encodeURIComponent(category)}` },
        ];
      }
      return [{ label: 'Home', to: '/' }, { label: 'Products', to: '/products' }];
    }

    if (path.startsWith('/orders')) return [{ label: 'Home', to: '/' }, { label: 'Orders', to: '/orders' }];
    if (path.startsWith('/notifications')) return [{ label: 'Home', to: '/' }, { label: 'Notifications', to: '/notifications' }];
    if (path.startsWith('/profile')) return [{ label: 'Home', to: '/' }, { label: 'Profile', to: '/profile' }];
    if (path.startsWith('/checkout')) return [{ label: 'Home', to: '/' }, { label: 'Checkout', to: '/checkout' }];
    if (path.startsWith('/login')) return [{ label: 'Home', to: '/' }, { label: 'Login', to: '/login' }];

    const parts = path.split('/').filter(Boolean).map((part) => part.replace(/[-_]/g, ' '));
    return parts.map((part) => ({
      label: part.charAt(0).toUpperCase() + part.slice(1),
      to: `/${part}`,
    }));
  }, [location.pathname, searchParams, categoryMap]);

  return (
    <header className="pwa-only pwa-compact-header w-full max-w-full overflow-x-hidden" data-testid="pwa-compact-header">
      <div className="pwa-compact-header__row">
        <div className="pwa-compact-header__brand" onClick={() => navigate('/')}>
          <span className="pwa-compact-header__dot" />
          <span className="pwa-compact-header__title">Amorlias</span>
        </div>
        <div className="pwa-compact-header__actions">
          {user?.role === 'admin' && (
            <Button
              variant="ghost"
              size="icon"
              className="pwa-compact-header__icon text-primary"
              onClick={() => {
                haptic();
                navigate('/admin');
              }}
              aria-label="Admin Panel"
            >
              <LayoutDashboard className="w-5 h-5" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="pwa-compact-header__icon"
            onClick={() => {
              haptic();
              navigate('/notifications');
            }}
            aria-label="Notifications"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="pwa-compact-header__badge">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="pwa-compact-header__icon"
            onClick={() => {
              haptic();
              navigate('/checkout');
            }}
            aria-label="Cart"
          >
            <ShoppingCart className="w-5 h-5" />
            {getItemCount() > 0 && (
              <span className="pwa-compact-header__badge">{getItemCount()}</span>
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="pwa-compact-header__icon"
            onClick={() => {
              haptic();
              navigate(user ? '/profile' : '/login');
            }}
            aria-label="Profile"
          >
            <User className="w-5 h-5" />
          </Button>
        </div>
      </div>
      <form className="pwa-compact-header__search" onSubmit={handleSearch}>
        <Search className="pwa-compact-header__search-icon" />
        <Input
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder="Search products"
          className="pwa-compact-header__input pl-11"
        />
      </form>
      {crumbs.length > 0 && (
        <div className="pwa-compact-header__crumbs" aria-label="Breadcrumb">
          {crumbs.map((crumb, index) => (
            <button
              key={`${crumb.label}-${index}`}
              type="button"
              className="pwa-compact-header__crumb"
              onClick={() => {
                haptic();
                navigate(crumb.to);
              }}
            >
              {crumb.label}
              {index < crumbs.length - 1 ? <span className="pwa-compact-header__crumb-sep">/</span> : null}
            </button>
          ))}
        </div>
      )}
      {chips.length > 0 && (
        <div className="pwa-compact-header__chips">
          {chips.map((chip) => (
            <button
              key={chip}
              type="button"
              className="pwa-compact-header__chip"
              onClick={() => {
                haptic();
                setSearchQuery(chip);
                navigate(`/products?search=${encodeURIComponent(chip)}`);
              }}
            >
              {chip}
            </button>
          ))}
        </div>
      )}
    </header>
  );
}
